import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AppleLogo } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Connessione Google / Apple Calendar condivisa tra CalendarPage e SidebarCalendar.
export function useCalendarConnections() {
  const { user, setUser } = useAuth();
  const [appleOpen, setAppleOpen] = useState(false);

  const refreshMe = async () => {
    const me = await api.get("/auth/me");
    setUser(me.data);
    localStorage.setItem("ht_user", JSON.stringify(me.data));
  };

  const connectGoogle = async () => {
    try {
      const r = await api.get("/calendar/connect-google", { params: { return_to: window.location.pathname } });
      if (r.data?.auth_url) {
        window.location.href = r.data.auth_url;
        return;
      }
      toast.error("URL di autorizzazione non disponibile");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore avvio connessione Google");
    }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm("Disconnettere Google Calendar? Gli eventi già sincronizzati resteranno sul tuo account Google.")) return;
    try {
      await api.post("/calendar/disconnect-google");
      toast.success("Google Calendar disconnesso");
      await refreshMe();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore disconnessione");
    }
  };

  const disconnectApple = async () => {
    if (!window.confirm("Disconnettere Apple Calendar?")) return;
    try {
      await api.post("/calendar/disconnect-apple");
      toast.success("Apple Calendar disconnesso");
      await refreshMe();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore disconnessione");
    }
  };

  // Gestione query params di ritorno dall'OAuth Google
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("google") === "connected") {
      toast.success("Google Calendar collegato con successo");
      refreshMe().catch(() => {});
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("google_error")) {
      toast.error("Connessione Google non riuscita: " + params.get("google_error"));
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { user, appleOpen, setAppleOpen, connectGoogle, disconnectGoogle, disconnectApple, refreshMe };
}

export function AppleConnectDialog({ open, onOpenChange, onConnected }) {
  const [appleForm, setAppleForm] = useState({ apple_id: "", app_password: "" });
  const [appleSubmitting, setAppleSubmitting] = useState(false);

  const connectApple = async (e) => {
    e && e.preventDefault();
    if (!appleForm.apple_id.includes("@") || appleForm.app_password.trim().length < 8) {
      toast.error("Inserisci Apple ID valido e app-specific password (min 8 caratteri)");
      return;
    }
    setAppleSubmitting(true);
    try {
      const r = await api.post("/calendar/connect-apple", {
        apple_id: appleForm.apple_id.trim(),
        app_password: appleForm.app_password.trim(),
      });
      toast.success(`Apple Calendar collegato (${r.data.calendar_count} calendari)`);
      await onConnected();
      onOpenChange(false);
      setAppleForm({ apple_id: "", app_password: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore connessione Apple");
    } finally { setAppleSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !appleSubmitting && onOpenChange(v)}>
      <DialogContent className="max-w-md" data-testid="apple-connect-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AppleLogo size={20} weight="fill" /> Collega Apple Calendar
          </DialogTitle>
          <DialogDescription>
            Sincronizza automaticamente i tuoi sopralluoghi su iCloud. Servono l'Apple ID e una
            <strong> app-specific password </strong> — <em>non</em> la password normale del tuo Apple ID.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={connectApple} className="space-y-3">
          <div>
            <Label htmlFor="apple-id">Apple ID (email)</Label>
            <Input
              id="apple-id" type="email" autoComplete="email" required
              placeholder="tuo.nome@icloud.com"
              value={appleForm.apple_id}
              onChange={(e) => setAppleForm(f => ({ ...f, apple_id: e.target.value }))}
              data-testid="apple-id-input"
            />
          </div>
          <div>
            <Label htmlFor="apple-app-pw">App-specific password</Label>
            <Input
              id="apple-app-pw" type="password" required autoComplete="off"
              placeholder="xxxx-xxxx-xxxx-xxxx"
              value={appleForm.app_password}
              onChange={(e) => setAppleForm(f => ({ ...f, app_password: e.target.value }))}
              data-testid="apple-app-password-input"
            />
            <div className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">
              Come generarla:
              <ol className="list-decimal list-inside space-y-0.5 mt-1">
                <li>
                  Vai su{" "}
                  <a href="https://appleid.apple.com" target="_blank" rel="noreferrer" className="text-purple-700 underline">
                    appleid.apple.com
                  </a>
                  {" "}e accedi con il tuo Apple ID.
                </li>
                <li>Attiva l'<strong>Autenticazione a due fattori</strong> se non è già attiva.</li>
                <li>Sezione <strong>Sign-In and Security → App-Specific Passwords</strong>.</li>
                <li>Clicca <strong>Generate an app-specific password</strong>, chiamala "Housetag" e copiala qui.</li>
              </ol>
              <p className="mt-1.5 text-amber-700">
                ⚠️ Se in futuro cambi la password Apple ID, tutte le app-specific password vengono revocate — dovrai riconnettere.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={appleSubmitting} data-testid="apple-cancel">
              Annulla
            </Button>
            <Button type="submit" className="ht-gradient border-0 text-white" disabled={appleSubmitting} data-testid="apple-connect-submit">
              {appleSubmitting ? "Verifica in corso…" : "Connetti"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
